import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { CalendarDays, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MyAgendaItem, MyAgendaList } from '@up2d8/contracts';
import { Card, cn, dayOf, monthGrid, monthOf, shiftMonth } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { uiLocale } from '../../shared/format';

/**
 * De kaart "Agenda" in de rechterkolom (replica-spec §10.2).
 *
 * Een maandrooster met een telbadge op de dagen waarop iets staat, en daaronder wat er aankomt.
 * Eén maand per aanroep (`GET /api/v1/me/agenda?month=2026-09`): zonder die grens zou elk blad van
 * het rooster de volledige agenda van het bedrijf ophalen.
 *
 * **De dagen worden op hun datumtekst gematcht en niet op een tijdstip.** Een evenement komt als
 * ISO-moment binnen; de cel waarin het hoort, wordt bepaald door de datum zoals de lezer die ziet.
 * Daarom `dayOf(new Date(item.startsAt))` en geen vergelijking tussen twee momenten: dat laatste
 * zet een toolbox van 23:30 op de volgende dag zodra de lezer in een andere zone zit dan de server.
 */

/** "ma di wo do vr za zo", zoals §10.2 ze schrijft: twee letters, hoofdletters, maandag eerst. */
const WEEKDAGEN = ['ma', 'di', 'wo', 'do', 'vr', 'za', 'zo'];

function MaandLabel({ month, locale }: { month: string; locale: string }) {
  const [jaar, maand] = month.split('-');
  const datum = new Date(Number(jaar), Number(maand) - 1, 1);
  return (
    <p
      data-testid="agenda-month-label"
      className="mb-2.5 text-center font-heading text-small font-medium text-text-secondary"
    >
      {datum.toLocaleDateString(locale, { month: 'long', year: 'numeric' })}
    </p>
  );
}

function EventRij({ item, locale }: { item: MyAgendaItem; locale: string }) {
  const { t } = useTranslation();
  const start = new Date(item.startsAt);
  const einde = new Date(item.endsAt);

  /*
    "Hele dag" wordt gerekend en niet opgeslagen: een evenement dat van middernacht tot middernacht
    loopt, is een hele dag. Er is geen `allDay`-veld in ons model, en er een bijverzinnen zou
    betekenen dat bestaande evenementen het missen.
  */
  const heleDag =
    start.getHours() === 0 &&
    start.getMinutes() === 0 &&
    einde.getTime() - start.getTime() >= 86_400_000 - 60_000;

  const tijd = heleDag
    ? t('agendaCard.allDay')
    : [
        start.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
        einde.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' }),
      ].join(' — ');

  return (
    <Link
      to="/app/agenda"
      data-testid="agenda-event"
      className="flex items-center gap-2.5 rounded-lg p-2 transition-colors hover:bg-background"
    >
      <span
        aria-hidden
        className="w-10 shrink-0 rounded-sm bg-background px-0 py-1 text-center leading-[1.1]"
      >
        <span className="block font-heading text-content font-bold text-text-primary">
          {start.getDate()}
        </span>
        <span className="block font-heading text-[9px] font-medium uppercase tracking-[0.05em] text-text-muted">
          {start.toLocaleDateString(locale, { month: 'short' }).replace('.', '')}
        </span>
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate font-heading text-small font-medium text-text-primary">
          {item.title}
        </span>
        <span className="flex items-center gap-1 truncate text-caption text-text-muted">
          <Clock aria-hidden className="h-2.5 w-2.5 shrink-0" />
          {item.location ? `${tijd} · ${item.location}` : tijd}
        </span>
      </span>
    </Link>
  );
}

export function AppAgendaCard() {
  const { t } = useTranslation();
  const { api, has } = useSession();
  const locale = uiLocale();

  const [month, setMonth] = useState(() => monthOf(new Date()));
  const vandaag = dayOf(new Date());

  const agenda = useQuery({
    queryKey: ['my-agenda', month],
    enabled: has('agenda.item.read'),
    queryFn: async () => {
      const { data, error } = await api.GET('/api/v1/me/agenda', {
        params: { query: { month } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return (data as MyAgendaList).items;
    },
  });

  if (!has('agenda.item.read')) return null;

  const items = agenda.data ?? [];

  // Hoeveel evenementen per dag: de telbadge op het rooster. Een `Map` en niet een filter per cel,
  // want dat zou tweeënveertig keer door de lijst lopen.
  const perDag = new Map<string, number>();
  for (const item of items) {
    const dag = dayOf(new Date(item.startsAt));
    perDag.set(dag, (perDag.get(dag) ?? 0) + 1);
  }

  const komende = items.filter((item) => new Date(item.endsAt).getTime() >= Date.now()).slice(0, 3);

  return (
    <Card compact data-testid="agenda-card" className="p-[18px]">
      <div className="mb-3.5 flex items-center justify-between gap-2">
        <h2 className="flex items-center gap-2 font-heading text-card-title font-semibold text-text-primary">
          <span
            aria-hidden
            className="flex h-[26px] w-[26px] items-center justify-center rounded-sm bg-brand-primary-tint text-brand-primary-dark"
          >
            <CalendarDays className="h-3.5 w-3.5" />
          </span>
          {t('agendaCard.title')}
        </h2>

        <div className="flex shrink-0 items-center gap-0.5">
          <button
            type="button"
            data-testid="agenda-prev"
            aria-label={t('agendaCard.previousMonth')}
            onClick={() => setMonth((huidig) => shiftMonth(huidig, -1))}
            className="flex h-[26px] w-[26px] items-center justify-center rounded-pill text-text-muted transition-colors hover:bg-background hover:text-brand-primary"
          >
            <ChevronLeft aria-hidden className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            data-testid="agenda-next"
            aria-label={t('agendaCard.nextMonth')}
            onClick={() => setMonth((huidig) => shiftMonth(huidig, 1))}
            className="flex h-[26px] w-[26px] items-center justify-center rounded-pill text-text-muted transition-colors hover:bg-background hover:text-brand-primary"
          >
            <ChevronRight aria-hidden className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <MaandLabel month={month} locale={locale} />

      {/*
        Zeven gelijke kolommen, maandag eerst. `aspect-square` op de cel: zonder dat worden de
        dagen ovaal zodra de kolom smaller is dan de regelhoogte, en dan is de gevulde cirkel van
        vandaag geen cirkel meer.
      */}
      <div data-testid="agenda-grid" className="grid grid-cols-7 gap-0.5">
        {WEEKDAGEN.map((dag) => (
          <span
            key={dag}
            aria-hidden
            className="pb-2 pt-1 text-center font-heading text-micro font-medium uppercase text-text-muted"
          >
            {dag}
          </span>
        ))}

        {monthGrid(month).map((cel) => {
          const aantal = perDag.get(cel.date) ?? 0;
          /*
            Vandaag krijgt de gevulde cirkel, maar alleen in de maand waar hij in hoort.

            Zonder `cel.inMonth` licht vandaag ook op in de naloop van de vorige maand en de
            voorloop van de volgende — dezelfde dag, grijs omdat hij buiten de maand valt, en
            toch met een cirkel erom. Gevonden door de test die terugbladert en dan geen cirkel
            meer verwacht.
          */
          const isVandaag = cel.inMonth && cel.date === vandaag;

          return (
            <span
              key={cel.date}
              data-testid="agenda-day"
              data-date={cel.date}
              data-today={isVandaag ? 'ja' : 'nee'}
              className={cn(
                'relative flex aspect-square items-center justify-center rounded-pill font-heading text-small font-medium transition-colors',
                isVandaag
                  ? 'bg-brand-primary font-semibold text-text-on-dark shadow-md'
                  : cel.inMonth
                    ? 'text-text-primary hover:bg-background'
                    : 'text-border',
              )}
            >
              {cel.day}
              {aantal > 0 ? (
                <span
                  data-testid="agenda-day-count"
                  className={cn(
                    'absolute right-px top-px flex h-3.5 min-w-3.5 items-center justify-center rounded-pill bg-brand-orange px-[3px] font-heading text-[8px] font-bold text-text-on-dark shadow-sm',
                    // Op de dag van vandaag staat de badge op een gevulde cirkel; zonder rand
                    // lopen de twee kleuren in elkaar over.
                    isVandaag && 'ring-[1.5px] ring-surface',
                  )}
                >
                  {aantal}
                </span>
              ) : null}
            </span>
          );
        })}
      </div>

      {/* De komende evenementen. Niets gepland betekent geen lijst en geen lijn: een lege ruimte
          onder een rooster leest als iets dat niet geladen is. */}
      {komende.length > 0 ? (
        <div className="mt-4 flex flex-col gap-0.5 border-t border-border-light pt-3.5">
          {komende.map((item) => (
            <EventRij key={item.id} item={item} locale={locale} />
          ))}
        </div>
      ) : null}
    </Card>
  );
}
